import React, { useEffect, useRef, useState, useMemo } from 'react';
import './index.css';
import { LineSeries, CandlestickSeries, createChart, ColorType, LineStyle, CrosshairMode } from 'lightweight-charts';
import { handleForbiddenResponse } from '../../utils/api';
import { usePairs } from '../../hooks/usePairs';
import { PairSelector } from '../PairSelector';
import { UpdateIcon } from "../shared/UpdateIcon.jsx";

const FAVORITES_STORAGE_KEY = 'favoritePairs';

const StarIcon = ({ filled }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

function getLanguageFromURL() {
  const regex = new RegExp('[\\\?&]lang=([^&#]*)');
  const results = regex.exec(window.location.search);
  return results === null ? null : decodeURIComponent(results[1].replace(/\+/g, ' '));
}


const FALLBACK_HEIGHT = 320;

const resolveDimensions = (container) => {
  if (!container) {
    return { width: window.innerWidth, height: Math.max(window.innerHeight * 0.6, FALLBACK_HEIGHT) };
  }
  const width = container.clientWidth || window.innerWidth;
  const height = container.clientHeight || Math.max(window.innerHeight * 0.68, FALLBACK_HEIGHT);
  return { width, height };
};

export const TVChartContainer = (props) => {
  const {
    data,
    colors: {
      backgroundColor = '#0f111d',
      lineColor = '#4a68ff',
      textColor = '#f0f4ff',
      areaTopColor = '#4a68ff',
      areaBottomColor = 'rgba(41, 104, 255, 0.28)',
    } = {},
  } = props;

  const chartContainerRef = useRef(null);

  const [initialData, setData] = useState(null);
  const [orders, setOrders] = useState(null);
  const [symbol, setSymbol] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const priceRange = '30';
  const lookBackDays = '5';
  const serverUrl = 'https://tkachev-trade.com/api';

  const [ordersCount, setOrdersCount] = useState(20);
  const [amountFrom, setAmountFrom] = useState(null);
  const [amountTo, setAmountTo] = useState(null);
  const [appliedOrdersCount, setAppliedOrdersCount] = useState(20);
  const [appliedAmountFrom, setAppliedAmountFrom] = useState(null);
  const [appliedAmountTo, setAppliedAmountTo] = useState(null);
  const [ordersFiltersVersion, setOrdersFiltersVersion] = useState(0);
  const [settingsExpanded, setSettingsExpanded] = useState(false);

  const [tooltipAsks, setTooltipAsks] = useState(null);
  const [tooltipBids, setTooltipBids] = useState(null);
  const [labelPositionAsks, setLabelPositionAsks] = useState({ x: 0, y: 0 });
  const [labelPositionBids, setLabelPositionBids] = useState({ x: 0, y: 0 });
  const [favorites, setFavorites] = useState(() => {
    try {
      const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const { data: pairsData, loading: pairsLoading, error: pairsError } = usePairs();
  const pairsInfo = pairsData?.pairsInfo ?? [];

  const sortedPairsInfo = useMemo(() => {
    return [...pairsInfo].sort((a, b) => {
      const aIsFavorite = favorites.includes(a.symbol);
      const bIsFavorite = favorites.includes(b.symbol);

      if (aIsFavorite && !bIsFavorite) return -1;
      if (!aIsFavorite && bIsFavorite) return 1;
      return 0;
    });
  }, [pairsInfo, favorites]);
  const selectedPair = pairsInfo.find((pair) => pair.symbol === symbol);
  const activeSymbol = selectedPair?.symbol ?? symbol;
  const rawMinOrder = selectedPair !== undefined ? Number(selectedPair.minOrder ?? 0) : null;
  const rawMaxOrder = selectedPair !== undefined ? Number(selectedPair.maxOrder ?? selectedPair.minOrder ?? 0) : null;
  const minOrder = Number.isFinite(rawMinOrder) ? rawMinOrder : 0;
  const maxOrderCandidate = Number.isFinite(rawMaxOrder) ? rawMaxOrder : minOrder;
  const maxOrder = Math.max(maxOrderCandidate, minOrder);
  const amountStepRaw = selectedPair ? Number(selectedPair.step ?? 1) : 1;
  const amountStep = Number.isFinite(amountStepRaw) && amountStepRaw > 0 ? amountStepRaw : 1;
  const amountRangeDisabled = !selectedPair || !Number.isFinite(rawMinOrder) || !Number.isFinite(rawMaxOrder) || minOrder === maxOrder;

  useEffect(() => {
    const handleStorageChange = () => {
      try {
        const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
        setFavorites(stored ? JSON.parse(stored) : []);
      } catch {
        setFavorites([]);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Проверяем изменения каждую секунду (для изменений в том же окне)
    const interval = setInterval(handleStorageChange, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const clampAmountValue = (value) => {
    if (value === null || amountRangeDisabled) {
      return null;
    }
    if (!Number.isFinite(value)) {
      return null;
    }
    const clamped = Math.min(Math.max(value, minOrder), maxOrder);
    if (!Number.isFinite(clamped)) {
      return null;
    }
    const hasMin = Number.isFinite(minOrder);
    const hasMax = Number.isFinite(maxOrder);
    const minInt = hasMin ? Math.ceil(minOrder) : null;
    const maxInt = hasMax ? Math.floor(maxOrder) : null;
    let normalized = Math.round(clamped);
    if (!Number.isFinite(normalized)) {
      return null;
    }
    if (maxInt !== null && minInt !== null && minInt > maxInt) {
      return normalized;
    }
    if (minInt !== null) {
      normalized = Math.max(normalized, minInt);
    }
    if (maxInt !== null) {
      normalized = Math.min(normalized, maxInt);
    }
    return normalized;
  };

  const amountRangeError = !amountRangeDisabled && amountFrom !== null && amountTo !== null && amountFrom > amountTo;
  const isApplyDisabled = amountRangeDisabled || amountRangeError;

  useEffect(() => {
    if (!pairsInfo.length) {
      return;
    }
    if (!symbol) {
      setSymbol(pairsInfo[0].symbol);
      return;
    }
    const exists = pairsInfo.some((pair) => pair.symbol === symbol);
    if (!exists) {
      setSymbol(pairsInfo[0].symbol);
    }
  }, [pairsInfo, symbol]);

  useEffect(() => {
    setAmountFrom(null);
    setAmountTo(null);
    setAppliedAmountFrom(null);
    setAppliedAmountTo(null);
  }, [activeSymbol]);

  useEffect(() => {
    if (amountRangeDisabled) {
      if (amountFrom !== null) {
        setAmountFrom(null);
      }
      if (amountTo !== null) {
        setAmountTo(null);
      }
      return;
    }

    if (amountTo !== null) {
      const normalizedTo = clampAmountValue(amountTo);
      if (normalizedTo !== amountTo) {
        setAmountTo(normalizedTo);
      }
    }

    if (amountFrom !== null) {
      const normalizedFrom = clampAmountValue(amountFrom);
      if (normalizedFrom !== amountFrom) {
        setAmountFrom(normalizedFrom);
      }
    }
  }, [amountRangeDisabled, amountFrom, amountTo, minOrder, maxOrder]);

  useEffect(() => {
    if (!amountRangeDisabled) {
      return;
    }
    if (appliedAmountFrom !== null) {
      setAppliedAmountFrom(null);
    }
    if (appliedAmountTo !== null) {
      setAppliedAmountTo(null);
    }
  }, [amountRangeDisabled, appliedAmountFrom, appliedAmountTo]);

  useEffect(() => {
    if (!activeSymbol) {
      return;
    }
    let isCancelled = false;
    setData(null);

    const token = localStorage.getItem('token');

    fetch(`${serverUrl}/chart/kline?symbol=${activeSymbol}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (handleForbiddenResponse(response)) {
          return Promise.reject(new Error('Forbidden'));
        }
        return response.json();
      })
      .then((json) => {
        if (!isCancelled) {
          const items = json?.response?.items ?? [];
          setData(items);
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          console.error('Failed to fetch kline data:', error);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [serverUrl, activeSymbol, reloadKey]);

  useEffect(() => {
    if (!activeSymbol) {
      return;
    }
    let isCancelled = false;
    setOrders(null);
    setTooltipAsks(null);
    setTooltipBids(null);

    const token = localStorage.getItem('token');
    let url = `${serverUrl}/chart/orders?symbol=${activeSymbol}&priceRange=${priceRange}&ordersCount=${appliedOrdersCount}&lookBackDays=${lookBackDays}`;
    if (appliedAmountFrom !== null) {
      url += `&amountFrom=${appliedAmountFrom}`;
    }
    if (appliedAmountTo !== null) {
      url += `&amountTo=${appliedAmountTo}`;
    }

    fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (handleForbiddenResponse(response)) {
          return Promise.reject(new Error('Forbidden'));
        }
        return response.json();
      })
      .then((json) => {
        if (!isCancelled) {
          const responseData = json?.response ?? null;
          setOrders(responseData);
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          console.error('Failed to fetch orders data:', error);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [
    serverUrl,
    activeSymbol,
    reloadKey,
    ordersFiltersVersion,
    priceRange,
    appliedOrdersCount,
    lookBackDays,
    appliedAmountFrom,
    appliedAmountTo,
  ]);

  useEffect(() => {
    const container = chartContainerRef.current;
    if (!container) {
      return undefined;
    }

    const { width, height } = resolveDimensions(container);

    const chart = createChart(container, {
      layout: {
        background: { type: ColorType.Solid, color: backgroundColor },
        textColor,
      },
      grid: {
        vertLines: {
          color: '#6d6a6aff',   // vertical grid lines
          style: 1,           // solid (0 = none, 1 = solid, 2 = dotted, etc.)
        },
        horzLines: {
          color: '#6d6a6aff',   // horizontal grid lines
          style: 1,
        },
      },
      width,
      height,
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        fixRightEdge: true,
      },
      handleScroll: {},
      rightPriceScale: {
        autoScale: false,
        ticksVisible: true,
      },
      crosshair: {
        mode: CrosshairMode.Normal,  // free crosshair
      },
    });

    const resize = () => {
      const nextDimensions = resolveDimensions(container);
      chart.applyOptions({
        width: nextDimensions.width,
        height: nextDimensions.height,
      });
    };

    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    window.addEventListener('resize', resize);
    if (resizeObserver) {
      resizeObserver.observe(container);
    }

    chart.timeScale().fitContent();

    const priceFmt = {
      type: 'price',
      precision: selectedPair?.precision ?? 4,
      minMove: selectedPair?.step ?? 0.0001,
    };

    const newSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderVisible: false,
      wickUpColor: '#26a69a',
      wickDownColor: '#ef5350',
      priceScaleId: 'right',
      priceFormat: priceFmt,
    });

    if (initialData) {
      newSeries.setData(initialData);
    }

    function distancePointToSegment(px, py, x1, y1, x2, y2) {
      const dx = x2 - x1;
      const dy = y2 - y1;
      if (dx === 0 && dy === 0) return Math.hypot(px - x1, py - y1);

      const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy)));
      const projX = x1 + t * dx;
      const projY = y1 + t * dy;

      return Math.hypot(px - projX, py - projY);
    }

    function formatUSDShort(amount) {
      if (amount === null || amount === undefined || isNaN(amount)) return "";

      const absAmount = Math.abs(amount);
      let formatted;

      if (absAmount >= 1e9) {
        formatted = (amount / 1e9).toFixed(1) + " Млрд";
      } else if (absAmount >= 1e6) {
        formatted = (amount / 1e6).toFixed(1) + " Млн";
      } else if (absAmount >= 1e3) {
        formatted = (amount / 1e3).toFixed(1) + " Тыс";
      } else {
        formatted = amount.toFixed(2);
      }
      return formatted.replace(".", ",");
    }

    const asks = orders?.asks ?? [];
    const bids = orders?.bids ?? [];

    const askLines = createLinesAndTooltip({
      orders: asks,
      chart,
      setTooltip: setTooltipAsks,
      setLabelPosition: setLabelPositionAsks,
      priceFmt,
    });

    const bidLines = createLinesAndTooltip({
      orders: bids,
      chart,
      setTooltip: setTooltipBids,
      setLabelPosition: setLabelPositionBids,
      priceFmt,
    });

    function createLinesAndTooltip({
      orders,
      chart,
      setTooltip,
      setLabelPosition,
      priceFmt,
    }) {
      const lines = orders.map((element) => {
        const line = chart.addSeries(LineSeries, {
          color: element.color,
          lineStyle: LineStyle.Solid,
          lineWidth: 4,
          priceLineVisible: false,
          lastValueVisible: false,
          priceFormat: priceFmt,
        });

        line.setData([
          { value: element.price, time: element.start },
          { value: element.price, time: element.end },
        ]);

        return { line, element };
      });

      chart.subscribeCrosshairMove((param) => {
        if (!param?.point) return;

        const crosshairX = param.point.x;
        const crosshairY = param.point.y;

        let bestHit = null;
        let bestDist = Infinity;
        const tolerance = 3;

        lines.forEach(({ line, element }) => {
          const x1 = chart.timeScale().timeToCoordinate(element.start);
          const y1 = line.priceToCoordinate(element.price);
          const x2 = chart.timeScale().timeToCoordinate(element.end);
          const y2 = line.priceToCoordinate(element.price);

          if ([x1, y1, x2, y2].some((v) => typeof v !== "number")) return;

          const dist = distancePointToSegment(crosshairX, crosshairY, x1, y1, x2, y2);

          if (dist <= tolerance && dist < bestDist) {
            bestDist = dist;
            bestHit = {
              content: `${formatUSDShort(element.amount * element.price)}`,
              x: crosshairX,
              y: crosshairY,
            };
          }
        });

        if (bestHit) {
          setTooltip({ content: bestHit.content });
          setLabelPosition({ x: bestHit.x, y: bestHit.y });
        } else {
          setTooltip(null);
        }
      });

      return lines;
    }

    return () => {
      window.removeEventListener('resize', resize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      chart.remove();
    };
  }, [
    data,
    backgroundColor,
    lineColor,
    textColor,
    areaTopColor,
    areaBottomColor,
    initialData,
    orders,
    selectedPair?.precision,
    selectedPair?.step,
  ]);

  const Tooltip = ({ content, x, y }) => {
    return (
      <div
        style={{
          position: 'absolute',
          top: `${y - 30}px`,  // Adjust the Y position
          left: `${x}px`, // Adjust the X position
          background: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          padding: '5px',
          borderRadius: '5px',
          fontSize: '12px',
          pointerEvents: 'none', // So it doesn't block interaction with the chart
          zIndex: 9999, // Ensure the tooltip is above other elements
        }}
      >
        {content}
      </div>
    );
  };

  const handlePairChange = (nextSymbol) => {
    setSymbol(nextSymbol);
  };

  const handleOrdersCountChange = (event) => {
    const value = Number(event.target.value);
    if (Number.isNaN(value)) {
      return;
    }
    setOrdersCount(value);
  };

  const handleAmountFromChange = (event) => {
    const nextValue = Number(event.target.value);
    if (Number.isNaN(nextValue)) {
      setAmountFrom(null);
      return;
    }
    const normalizedValue = clampAmountValue(nextValue);
    setAmountFrom(normalizedValue);
  };

  const handleAmountToChange = (event) => {
    const nextValue = Number(event.target.value);
    if (Number.isNaN(nextValue)) {
      setAmountTo(null);
      return;
    }
    const normalizedValue = clampAmountValue(nextValue);
    setAmountTo(normalizedValue);
  };

  const handleAmountFromReset = () => {
    setAmountFrom(null);
  };

  const handleAmountToReset = () => {
    setAmountTo(null);
  };

  const handleToggleSettings = () => {
    setSettingsExpanded((prev) => !prev);
  };

  const handleApplyFilters = () => {
    const nextAmountTo = clampAmountValue(amountTo);
    const nextAmountFrom = clampAmountValue(amountFrom);

    setAmountTo(nextAmountTo);
    setAmountFrom(nextAmountFrom);

    setAppliedOrdersCount(ordersCount);
    setAppliedAmountTo(nextAmountTo);
    setAppliedAmountFrom(nextAmountFrom);

    setOrdersFiltersVersion((prev) => prev + 1);

    handleToggleSettings();
  };

  const handleRefresh = () => {
    if (!activeSymbol) {
      return;
    }
    setReloadKey((prev) => prev + 1);
  };

  const toggleFavorite = () => {
    if (!activeSymbol) return;

    setFavorites((prev) => {
      const newFavorites = prev.includes(activeSymbol)
        ? prev.filter((s) => s !== activeSymbol)
        : [...prev, activeSymbol];

      try {
        localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(newFavorites));
      } catch (error) {
        console.error('Failed to save favorites:', error);
      }

      return newFavorites;
    });
  };

  const isFavorite = activeSymbol && favorites.includes(activeSymbol);

  return (
    <div className="chart-panel">
      <div className="chart-panel__search">
        <PairSelector
          pairsInfo={pairsInfo}
          value={symbol}
          onChange={handlePairChange}
          loading={pairsLoading}
          error={pairsError?.message}
        />
      </div>
      <div className="chart-panel__header">
        <div className="chart-panel__selector">
          <select
            className="chart-panel__dropdown"
            value={symbol}
            onChange={(e) => handlePairChange(e.target.value)}
            disabled={pairsLoading}
          >
            {sortedPairsInfo.map((pair) => {
              const isFavorite = favorites.includes(pair.symbol);
              return (
                <option key={pair.symbol} value={pair.symbol}>
                  {isFavorite ? '★ ' : ''}{pair.symbol}
                </option>
              );
            })}
          </select>
        </div>
        <div className="chart-panel__actions">
          <button
            type="button"
            className={`chart-panel__settings-button${settingsExpanded ? ' chart-panel__settings-button--active' : ''} flex-1`}
            onClick={handleToggleSettings}
            aria-pressed={settingsExpanded}
          >
            Настройки
          </button>
          <button
            type="button"
            className={`chart-panel__favorite-button${isFavorite ? ' chart-panel__favorite-button--active' : ''}`}
            onClick={toggleFavorite}
            title={isFavorite ? 'Убрать из избранного' : 'Добавить в избранное'}
          >
            <StarIcon filled={isFavorite} />
          </button>
          <button
            type="button"
            className="chart-panel__update-button"
            onClick={handleRefresh}
            title={`Update ${activeSymbol || ''}`}
          >
            <UpdateIcon />
          </button>
        </div>
      </div>
      {settingsExpanded && (
        <div className="chart-panel__settings">
          <div className="chart-panel__setting">
            <div className="chart-panel__setting-header">
              <span className="chart-panel__setting-label">Кол-во</span>
              <span className="chart-panel__setting-value">{ordersCount}</span>
            </div>
            <input
              type="range"
              min="20"
              max="100"
              step="1"
              value={ordersCount}
              onChange={handleOrdersCountChange}
              className="chart-panel__range"
            />
          </div>
          <div className={`chart-panel__setting${amountRangeError ? ' chart-panel__setting--error' : ''}`}>
            <div className="chart-panel__setting-header">
              <span className="chart-panel__setting-label">Мин Объём</span>
              <span className="chart-panel__setting-value">{amountFrom ?? '--'}</span>
            </div>
            <div className="chart-panel__setting-control">
              <input
                type="range"
                min={minOrder}
                max={maxOrder}
                step={amountStep}
                value={amountFrom ?? minOrder}
                onChange={handleAmountFromChange}
                className="chart-panel__range"
                disabled={amountRangeDisabled}
              />
            </div>
          </div>
          <div className={`chart-panel__setting${amountRangeError ? ' chart-panel__setting--error' : ''}`}>
            <div className="chart-panel__setting-header">
              <span className="chart-panel__setting-label">Макс Объём</span>
              <span className="chart-panel__setting-value">{amountTo ?? '--'}</span>
            </div>
            <div className="chart-panel__setting-control">
              <input
                type="range"
                min={minOrder}
                max={maxOrder}
                step={amountStep}
                value={amountTo ?? maxOrder}
                onChange={handleAmountToChange}
                className="chart-panel__range"
                disabled={amountRangeDisabled}
              />
            </div>
          </div>
          <div className="chart-panel__settings-actions">
            <button
              type="button"
              className="chart-panel__settings-apply"
              onClick={handleApplyFilters}
              disabled={isApplyDisabled}
            >
              Применить
            </button>
          </div>
        </div>
      )}
      <div className="chart-panel__body">
        <div
          ref={chartContainerRef}
          className="chart-panel__canvas"
        />
        {tooltipAsks && (
          <Tooltip content={tooltipAsks.content} x={labelPositionAsks.x} y={labelPositionAsks.y} />
        )}
        {tooltipBids && (
          <Tooltip content={tooltipBids.content} x={labelPositionBids.x} y={labelPositionBids.y} />
        )}
      </div>
    </div>
  );
};

